import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  Polyline,
  useGoogleMap,
  OverlayViewF,
} from "@react-google-maps/api";
import { Alert, Box, CircularProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { MAP_CONTAINER_STYLE, MAP_OPTIONS } from "../../constants";
import useGoogleMapsLoader from "../../hooks/useGoogleMapsLoader";
import { useThemeMode } from "../../theme";
import { Tooltip } from "..";
import { getMarker, getLabelColor } from "../../utils";
import {
  buildDiversionRoutePath,
  buildMapBoundsPoints,
  buildRecordedGpsPath,
  DIVERSION_ROUTE_OPTIONS,
  PLANNED_ROUTE_OPTIONS,
  RECORDED_GPS_TRACK_OPTIONS,
  toLatLng,
} from "./Map.helpers";

type Props = {
  waypoints: Waypoint[];
  recorded_gps_track?: GPSPoint[];
  gpsTrack?: GPSPoint[];
  routePoints?: RoutePoint[];
  onMarkerClick: (id: string) => void;
  activeMarker: string;
};

type AdvancedMarkerProps = {
  id: string;
  position: google.maps.LatLngLiteral;
  icon: string;
  labelText: string;
  labelClassName: string;
  title: string;
  onClick: (id: string) => void;
  markerLibrary: google.maps.MarkerLibrary | null;
  onHoverChange?: (id: string | null) => void;
};

type WaypointMarker = {
  waypoint: Waypoint;
  position: google.maps.LatLngLiteral;
};

export { RECORDED_GPS_TRACK_OPTIONS };

const getPathCenter = (
  path: google.maps.LatLngLiteral[]
): google.maps.LatLngLiteral => {
  const first = path[0];
  const last = path[path.length - 1];

  return {
    lat: (first.lat + last.lat) / 2,
    lng: (first.lng + last.lng) / 2,
  };
};

const createMarkerContent = (
  icon: string,
  labelText: string,
  labelClassName: string
) => {
  if (typeof document === "undefined") {
    return null;
  }

  const container = document.createElement("div");
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";

  const labelElement = document.createElement("span");
  labelElement.className = labelClassName;
  labelElement.textContent = labelText;
  container.appendChild(labelElement);

  const iconElement = document.createElement("img");
  iconElement.src = icon;
  iconElement.alt = labelText;
  iconElement.style.width = "28px";
  iconElement.style.height = "28px";
  iconElement.style.objectFit = "contain";
  container.appendChild(iconElement);

  return container;
};

const MapViewportFit = ({
  boundsPoints,
}: {
  boundsPoints: google.maps.LatLngLiteral[];
}) => {
  const map = useGoogleMap();

  useEffect(() => {
    if (
      !map ||
      !boundsPoints.length ||
      typeof google === "undefined" ||
      !google.maps?.LatLngBounds
    ) {
      return;
    }

    if (boundsPoints.length === 1) {
      map.setCenter(boundsPoints[0]);
      map.setZoom(8);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    boundsPoints.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 48);
  }, [boundsPoints, map]);

  return null;
};

const AdvancedMarker = ({
  id,
  position,
  icon,
  labelText,
  labelClassName,
  title,
  onClick,
  markerLibrary,
  onHoverChange,
}: AdvancedMarkerProps) => { 
  const map = useGoogleMap();

  const content = useMemo(
    () =>
      createMarkerContent(
        icon,
        labelText,
        `marker-label ${labelClassName}`.trim()
      ),
    [icon, labelClassName, labelText]
  );

  useEffect(() => {
    const MarkerElement = markerLibrary?.AdvancedMarkerElement;

    if (!map || !content || !MarkerElement) {
      return undefined;
    }

    // Skip marker creation if position has invalid coordinates
    if (
      typeof position?.lat !== "number" ||
      typeof position?.lng !== "number" ||
      !Number.isFinite(position.lat) ||
      !Number.isFinite(position.lng)
    ) {
      return undefined;
    }

    let marker: google.maps.marker.AdvancedMarkerElement | null = null;
    let clickListener: google.maps.MapsEventListener | null = null;
    let hoverInListener: google.maps.MapsEventListener | null = null;
    let hoverOutListener: google.maps.MapsEventListener | null = null;

    marker = new MarkerElement({
      map,
      position,
      title,
      content,
      zIndex: 2,
    }) as google.maps.marker.AdvancedMarkerElement;

    clickListener = marker.addListener("click", () => onClick(id));
    hoverInListener = marker.addListener("mouseover", () =>
      onHoverChange?.(id)
    );
    hoverOutListener = marker.addListener("mouseout", () =>
      onHoverChange?.(null)
    );

    return () => {
      if (clickListener) {
        clickListener.remove();
      }
      if (hoverInListener) {
        hoverInListener.remove();
      }
      if (hoverOutListener) {
        hoverOutListener.remove();
      }
      if (marker) {
        marker.map = null;
      }
    };
  }, [content, id, map, markerLibrary, onClick, onHoverChange, position, title]);

  return null;
};

export const Map: FunctionComponent<Props> = ({
  waypoints,
  recorded_gps_track,
  gpsTrack,
  routePoints,
  onMarkerClick,
  activeMarker,
}) => {
  const { mode: themeMode } = useThemeMode();
  const waypointList = waypoints ?? [];
  const recordedGpsTrack = recorded_gps_track ?? gpsTrack ?? [];
  const waypointMarkers = useMemo(
    () =>
      waypointList.reduce<WaypointMarker[]>((acc, waypoint) => {
        const coordinates = toLatLng(waypoint.coordinates as unknown as {
          lat?: number | string | null;
          lng?: number | string | null;
        });

        if (coordinates) {
          acc.push({
            waypoint,
            position: coordinates,
          });
        }

        return acc;
      }, []),
    [waypointList]
  );
  const waypointPath = useMemo(
    () => waypointMarkers.map((marker) => marker.position),
    [waypointMarkers]
  );
  const recordedGpsPath = useMemo(
    () => buildRecordedGpsPath(recordedGpsTrack),
    [recordedGpsTrack]
  );
  const hasRecordedGpsTrack = recordedGpsPath.length >= 2;
  const diversionRoutePath = useMemo(
    () => buildDiversionRoutePath(waypointList),
    [waypointList]
  );

  const plannedRoutePath = useMemo(() => {
    const ppsRoutePoints = (routePoints ?? [])
      .map((point) => {
        const lat =
          typeof point.latitude === "number"
            ? point.latitude
            : Number.parseFloat(String(point.latitude ?? ""));
        const lng =
          typeof point.longitude === "number"
            ? point.longitude
            : Number.parseFloat(String(point.longitude ?? ""));

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }

        return { lat, lng };
      })
      .filter((coord): coord is google.maps.LatLngLiteral => coord !== null);

    if (ppsRoutePoints.length) {
      return ppsRoutePoints;
    }

    return waypointPath;
  }, [routePoints, waypointPath]);

  const boundsPoints = useMemo(
    () =>
      buildMapBoundsPoints(
        plannedRoutePath,
        recordedGpsPath,
        waypointPath,
        diversionRoutePath
      ),
    [plannedRoutePath, recordedGpsPath, waypointPath, diversionRoutePath]
  );

  const hasAnyPath = boundsPoints.length > 0;

  const center = useMemo(() => {
    if (boundsPoints.length) {
      return getPathCenter(boundsPoints);
    }

    return { lat: 0, lng: 0 };
  }, [boundsPoints]);

  const {
    startLoading,
    status: mapsApiStatus,
    googleMapsApiKey,
    isGoogleMapsApiKeyLoading,
    isGoogleMapsApiKeyError,
    isMapsApiLoaded,
    markerLibrary,
    mapsApiError,
    googleMapsMapId,
    googleMapsLightMapId,
    googleMapsDarkMapId,
  } = useGoogleMapsLoader();
  const themeMapId =
    (themeMode === "dark" ? googleMapsDarkMapId : googleMapsLightMapId) ||
    googleMapsMapId ||
    undefined;
  const [mapType, setMapType] = useState<"roadmap" | "satellite">("roadmap");
  const mapOptions = useMemo(
    () => ({ ...MAP_OPTIONS, mapId: themeMapId, mapTypeId: mapType }),
    [mapType, themeMapId],
  );
  const [hasRequestedMap, setHasRequestedMap] = useState(false);
  const [hoveredWaypointId, setHoveredWaypointId] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const handleMarkerClick = useCallback((id: string) => onMarkerClick(id), [onMarkerClick]);

  const formatFuelValue = (value?: number | null) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    return "-";
  };

  const hoveredWaypoint = useMemo(
    () =>
      waypointMarkers.find((marker) => marker.waypoint.id === hoveredWaypointId) ||
      null,
    [hoveredWaypointId, waypointMarkers]
  );
  const activeWaypoint = useMemo(
    () =>
      waypointMarkers.find((marker) => marker.waypoint.id === activeMarker) ||
      null,
    [activeMarker, waypointMarkers]
  );

  useEffect(() => {
    if (!mapContainerRef.current || hasRequestedMap) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasRequestedMap(true);
            startLoading();
          }
        });
      },
      { rootMargin: "200px" }
    );

    observer.observe(mapContainerRef.current);

    return () => observer.disconnect();
  }, [hasAnyPath, hasRequestedMap, startLoading]);

  if (!hasAnyPath) {
    return null;
  }

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        position: "relative",
        minHeight: MAP_CONTAINER_STYLE.height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!hasRequestedMap ? (
        <CircularProgress size={32} />
      ) : isGoogleMapsApiKeyLoading ||
        mapsApiStatus === "loading" ||
        (googleMapsApiKey && !isMapsApiLoaded && !mapsApiError) ? (
        <CircularProgress size={32} />
      ) : googleMapsApiKey && isMapsApiLoaded && !mapsApiError ? (
        <>
          <GoogleMap
            key={`${themeMode}:${themeMapId || "default"}:${mapType}`}
            mapContainerStyle={MAP_CONTAINER_STYLE}
            options={mapOptions}
            zoom={6}
            center={center}
            onClick={() => onMarkerClick("")}
          >
            <MapViewportFit boundsPoints={boundsPoints} />
            {plannedRoutePath.length ? (
              <Polyline
                path={plannedRoutePath}
                options={PLANNED_ROUTE_OPTIONS}
              />
            ) : null}
            {diversionRoutePath.length ? (
              <Polyline
                path={diversionRoutePath}
                options={DIVERSION_ROUTE_OPTIONS}
              />
            ) : null}
            {waypointMarkers.map(({ waypoint, position }) => (
              <AdvancedMarker
                key={waypoint.id}
                id={waypoint.id}
                position={position}
                icon={getMarker(waypoint.isAfqSet, waypoint.isAtoSet)}
                labelText={waypoint.waypointName}
                labelClassName={getLabelColor(waypoint.isAfqSet, waypoint.isAtoSet)}
                title={waypoint.waypointName}
                onClick={handleMarkerClick}
                markerLibrary={markerLibrary}
                onHoverChange={setHoveredWaypointId}
              />
            ))}
            {activeWaypoint ? (
              <InfoWindow
                key={`${activeWaypoint.waypoint.id}-info`}
                position={activeWaypoint.position}
                onCloseClick={() => onMarkerClick("")}
              >
                <Tooltip {...activeWaypoint.waypoint} />
              </InfoWindow>
            ) : null}
            {hoveredWaypoint ? (
              <OverlayViewF
                position={hoveredWaypoint.position}
                mapPaneName="floatPane"
                getPixelPositionOffset={(width, height) => ({
                  x: -(width ?? 0) / 2,
                  y: -((height ?? 0) + 16),
                })}
              >
                <Box
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.75)",
                    color: "#fff",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    boxShadow: 3,
                    minWidth: "160px",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{hoveredWaypoint.waypoint.waypointName}</div>
                  <div>Planned fuel: {formatFuelValue(hoveredWaypoint.waypoint.EFQ)}</div>
                  <div>Min fuel: {formatFuelValue(hoveredWaypoint.waypoint.MFQ)}</div>
                  <div>Fuel on board: {formatFuelValue(hoveredWaypoint.waypoint.AFQ)}</div>
                </Box>
              </OverlayViewF>
            ) : null}
            {hasRecordedGpsTrack ? (
              <Polyline
                path={recordedGpsPath}
                options={RECORDED_GPS_TRACK_OPTIONS}
              />
            ) : null}
          </GoogleMap>
          <ToggleButtonGroup
            value={mapType}
            exclusive
            size="small"
            onChange={(_event, value) => value && setMapType(value)}
            aria-label="Map display type"
            sx={{ position: "absolute", top: 12, right: 12, bgcolor: "var(--panel-raised)" }}
          >
            <ToggleButton value="roadmap">Map</ToggleButton>
            <ToggleButton value="satellite">Satellite</ToggleButton>
          </ToggleButtonGroup>
        </>
      ) : (
        <Alert severity="warning" sx={{ width: "100%" }}>
          {isGoogleMapsApiKeyError || mapsApiError
            ? "Unable to load the Google Maps configuration. Map preview is temporarily unavailable."
            : "Google Maps API key is not configured. Contact an administrator to enable the map preview."}
        </Alert>
      )}
      {hasRecordedGpsTrack ? (
        <Box
          sx={{
            position: "absolute",
            right: 12,
            bottom: 12,
            backgroundColor: "var(--panel-raised)",
            border: "1px solid var(--line)",
            borderRadius: "4px",
            boxShadow: 2,
            color: "text.primary",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: 1.4,
            px: 1,
            py: 0.75,
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 18,
              height: 3,
              mr: 0.75,
              mb: "3px",
              backgroundColor: RECORDED_GPS_TRACK_OPTIONS.strokeColor,
              verticalAlign: "middle",
            }}
          />
          Aufgezeichneter GPS-Track
        </Box>
      ) : null}
    </Box>
  );
};
